import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { ArrowRight, ArrowUpRight, Package, Beaker, ShieldCheck } from "lucide-react";
import { fetcher } from "@/lib/api";
import { formatINR } from "@/lib/utils";

export default function ProductDetail() {
  const { slug } = useParams();
  const { data: p } = useSWR(slug ? `/products/${slug}` : null, fetcher);

  if (!p) {
    return (
      <div className="pt-40 pb-24 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="font-editorial text-4xl text-[#1D3A2F]">Loading…</div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24">
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-6">
        <div className="text-xs tracking-[0.16em] uppercase text-[#556B57] flex items-center gap-2">
          <Link to="/">Home</Link> /
          <Link to="/categories">Categories</Link> /
          <Link to={`/categories/${p.category}`}>{p.category_data?.name}</Link> /
          <span className="text-[#1D3A2F]">{p.name}</span>
        </div>
      </div>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14">
        {/* IMAGE */}
        <div className="relative">
          <div className="sticky top-28 rounded-3xl overflow-hidden bg-[#FDFBF7] aspect-square border border-[#D7D4CF]/40">
            <div className="absolute top-8 left-8 z-10">
              <div className="eyebrow text-[#1D3A2F]/50">{p.category_data?.name}</div>
              <div className="font-editorial text-xl text-[#1D3A2F] mt-1">Fresio Herbals</div>
            </div>
            <img
              src={p.image}
              alt={p.name}
              data-testid="product-hero-image"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* INFO */}
        <div className="lg:pt-6">
          {p.best_seller && (
            <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-full bg-[#1D3A2F] text-[#FDFBF7] text-[10px] tracking-[0.18em] uppercase">
              Bestseller
            </div>
          )}
          <h1 className="font-editorial text-5xl md:text-6xl text-[#1D3A2F] leading-[0.98]" data-testid="product-name">
            {p.name}
          </h1>
          <p className="mt-4 text-lg text-[#556B57] italic">{p.tagline}</p>

          {/* Price + MOQ chip */}
          <div className="mt-8 flex items-end justify-between border-y border-[#D7D4CF] py-6">
            <div>
              <div className="eyebrow mb-1">Sample from</div>
              <div className="font-editorial text-4xl text-[#1D3A2F]" data-testid="product-sample-price">
                {p.price_inr > 0 ? formatINR(p.price_inr) : "On request"}
              </div>
            </div>
            <div className="text-right">
              <div className="eyebrow mb-1">MOQ</div>
              <div className="font-editorial text-4xl text-[#1D3A2F]">
                {p.moq}
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/order-sample/${p.slug}`}
              data-testid="product-order-sample"
              className="inline-flex items-center gap-3 h-14 px-7 rounded-full bg-[#1D3A2F] text-[#FDFBF7] text-[13px] tracking-[0.16em] uppercase font-medium hover:bg-black transition"
            >
              Order Sample <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 h-14 px-7 rounded-full border border-[#1D3A2F]/30 text-[#1D3A2F] text-[13px] tracking-[0.16em] uppercase font-medium hover:border-[#1D3A2F] transition"
            >
              Request Quote <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Ingredients */}
          <Section title="Key Ingredients">
            <div className="flex flex-wrap gap-2">
              {p.ingredients.map((i) => (
                <span
                  key={i}
                  className="px-4 py-2 rounded-full bg-[#F7F4EE] border border-[#D7D4CF]/60 text-sm text-[#1D3A2F]"
                >
                  {i}
                </span>
              ))}
            </div>
          </Section>

          {/* Benefits */}
          <Section title="Benefits">
            <ul className="space-y-2">
              {p.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[#222222]/80">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#556B57] mt-2.5" />
                  {b}
                </li>
              ))}
            </ul>
          </Section>

          {/* Sizes */}
          <Section title="Packaging Sizes">
            <div className="flex flex-wrap gap-2">
              {p.sizes.map((s) => (
                <span
                  key={s}
                  className="px-4 py-2 rounded-full border border-[#1D3A2F]/20 text-sm text-[#1D3A2F]"
                >
                  {s}
                </span>
              ))}
            </div>
          </Section>

          {/* Manufacturing Chips */}
          <Section title="Available services">
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: Package, t: "Private Label" },
                { icon: Beaker, t: "OEM" },
                { icon: ShieldCheck, t: "Customization" },
              ].map(({ icon: Icon, t }) => (
                <div
                  key={t}
                  className="rounded-2xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-4 flex flex-col items-start gap-2"
                >
                  <Icon className="w-5 h-5 text-[#1D3A2F]" strokeWidth={1.4} />
                  <span className="text-sm text-[#1D3A2F] font-medium">{t}</span>
                </div>
              ))}
            </div>
          </Section>
        </div>
      </section>

      {/* Related */}
      {p.related?.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-32">
          <div className="eyebrow mb-4">Related products</div>
          <h2 className="font-editorial text-4xl text-[#1D3A2F] mb-10">
            You may also love.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
            {p.related.map((r) => (
              <Link
                key={r.slug}
                to={`/products/${r.slug}`}
                className="group"
                data-testid={`related-product-${r.slug}`}
              >
                <div className="relative rounded-3xl overflow-hidden bg-[#FDFBF7] aspect-[4/5] mb-4">
                  <img
                    src={r.image}
                    alt={r.name}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                </div>
                <div className="font-editorial text-xl text-[#1D3A2F]">{r.name}</div>
                <div className="text-xs text-[#556B57] mt-1">{r.tagline}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-10">
      <div className="eyebrow mb-4">{title}</div>
      {children}
    </div>
  );
}
